import type {
  MissionDefinition,
  MissionManager,
  MissionStateSnapshot
} from '../gameplay/missions';

// Long-lived so the player can comfortably read both the title and the
// flavor message. Two missions completing back-to-back will overwrite —
// `scheduleBannerHide` resets the timer and the banner re-pops.
const COMPLETION_BANNER_MS = 9000;

/**
 * Polished, read-only mission tracker pinned to the top-right of the
 * HUD. Subscribes to MissionManager and renders title / objective / hint
 * / progress. The HUD never decides mission state — it only mirrors the
 * snapshot.
 *
 * A second banner pops in briefly when a mission is completed or a new
 * mission begins, then fades out — independent of the persistent
 * tracker so progress never disappears mid-run.
 */
export class MissionTrackerHUD {
  readonly element: HTMLElement;

  private readonly card: HTMLElement;
  private readonly headerTagEl: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly objectiveEl: HTMLElement;
  private readonly progressWrap: HTMLElement;
  private readonly progressBar: HTMLElement;
  private readonly progressLabel: HTMLElement;
  private readonly hintEl: HTMLElement;

  private readonly banner: HTMLElement;
  private readonly bannerTagEl: HTMLElement;
  private readonly bannerTitleEl: HTMLElement;
  private readonly bannerMessageEl: HTMLElement;
  private bannerTimer: number | null = null;

  private readonly unsubscribes: Array<() => void> = [];

  constructor(manager: MissionManager) {
    this.element = document.createElement('div');
    this.element.className = 'mission-tracker';

    this.card = document.createElement('div');
    this.card.className = 'mission-tracker-card';

    this.headerTagEl = document.createElement('div');
    this.headerTagEl.className = 'mission-tracker-tag';
    this.headerTagEl.textContent = 'Active Mission';

    this.titleEl = document.createElement('h3');
    this.titleEl.className = 'mission-tracker-title';

    this.objectiveEl = document.createElement('div');
    this.objectiveEl.className = 'mission-tracker-objective';

    this.progressWrap = document.createElement('div');
    this.progressWrap.className = 'mission-tracker-progress';
    this.progressBar = document.createElement('div');
    this.progressBar.className = 'mission-tracker-progress-bar';
    const progressTrack = document.createElement('div');
    progressTrack.className = 'mission-tracker-progress-track';
    progressTrack.appendChild(this.progressBar);
    this.progressLabel = document.createElement('span');
    this.progressLabel.className = 'mission-tracker-progress-label';
    this.progressWrap.append(progressTrack, this.progressLabel);

    this.hintEl = document.createElement('div');
    this.hintEl.className = 'mission-tracker-hint';

    this.card.append(
      this.headerTagEl,
      this.titleEl,
      this.objectiveEl,
      this.progressWrap,
      this.hintEl
    );

    this.banner = document.createElement('div');
    this.banner.className = 'mission-tracker-banner';
    this.bannerTagEl = document.createElement('div');
    this.bannerTagEl.className = 'mission-tracker-banner-tag';
    this.bannerTitleEl = document.createElement('div');
    this.bannerTitleEl.className = 'mission-tracker-banner-title';
    this.bannerMessageEl = document.createElement('div');
    this.bannerMessageEl.className = 'mission-tracker-banner-message';
    this.banner.append(
      this.bannerTagEl,
      this.bannerTitleEl,
      this.bannerMessageEl
    );

    this.element.append(this.banner, this.card);

    this.unsubscribes.push(manager.subscribe((snap) => this.applyState(snap)));
  }

  /** Show the completion banner. Called from the manager hook in
   *  Application — independent of the regular state subscription so the
   *  underlying tracker can already show the next mission. */
  flashMissionCompleted(mission: MissionDefinition): void {
    this.bannerTagEl.textContent = 'Mission Complete';
    this.bannerTitleEl.textContent = mission.title;
    this.bannerMessageEl.textContent = mission.completionMessage ?? '';
    this.banner.classList.remove('is-visible', 'is-started');
    void this.banner.offsetWidth;
    this.banner.classList.add('is-visible');
    this.scheduleBannerHide();
  }

  flashMissionStarted(mission: MissionDefinition): void {
    this.bannerTagEl.textContent = 'New Mission';
    this.bannerTitleEl.textContent = mission.title;
    this.bannerMessageEl.textContent = mission.description;
    this.banner.classList.remove('is-visible');
    void this.banner.offsetWidth;
    this.banner.classList.add('is-visible', 'is-started');
    this.scheduleBannerHide();
  }

  /** Briefly highlight the card on step advance — small visual ping. */
  flashStepAdvance(): void {
    this.card.classList.remove('is-step-advance');
    void this.card.offsetWidth;
    this.card.classList.add('is-step-advance');
    window.setTimeout(() => {
      this.card.classList.remove('is-step-advance');
    }, 700);
  }

  dispose(): void {
    for (const unsub of this.unsubscribes) unsub();
    this.unsubscribes.length = 0;
    if (this.bannerTimer !== null) {
      window.clearTimeout(this.bannerTimer);
      this.bannerTimer = null;
    }
    this.element.remove();
  }

  private applyState(snap: MissionStateSnapshot): void {
    if (!snap.activeMissionId) {
      this.element.classList.remove('is-visible');
      return;
    }
    this.element.classList.add('is-visible');

    this.titleEl.textContent = snap.activeMissionTitle ?? '';
    this.objectiveEl.textContent = snap.objectiveText ?? '';

    if (snap.requiredCount && snap.requiredCount > 0) {
      this.progressWrap.classList.add('is-visible');
      const ratio = Math.max(
        0,
        Math.min(1, snap.currentCount / snap.requiredCount)
      );
      this.progressBar.style.width = `${ratio * 100}%`;
      this.progressLabel.textContent = `${snap.currentCount} / ${snap.requiredCount}`;
    } else {
      this.progressWrap.classList.remove('is-visible');
      this.progressBar.style.width = '0%';
      this.progressLabel.textContent = '';
    }

    if (snap.hintText) {
      this.hintEl.textContent = snap.hintText;
      this.hintEl.classList.add('is-visible');
    } else {
      this.hintEl.textContent = '';
      this.hintEl.classList.remove('is-visible');
    }
  }

  private scheduleBannerHide(): void {
    if (this.bannerTimer !== null) window.clearTimeout(this.bannerTimer);
    this.bannerTimer = window.setTimeout(() => {
      this.banner.classList.remove('is-visible');
      this.bannerTimer = null;
    }, COMPLETION_BANNER_MS);
  }
}
